import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { atlasRoutes } from "@/app/router/routes";
import {
  searchNoteSuggestionsInternal,
  NoteSuggestionSummaryInternalResponseSchema,
} from "@/pages/note-suggestions/shared/api";

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  flex: 1;
  max-width: 400px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e2e8f0;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  border-bottom: 1px solid #f1f5f9;
`;

const Tr = styled.tr`
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f8fafc;
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const IdCell = styled.span`
  font-family: "Berkeley Mono Variable", monospace;
  font-size: 12px;
  color: #3b82f6;
`;

const KindBadge = styled.span<{ kind: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ kind }) => {
    if (kind.includes("cleanup")) return "#dbeafe";
    if (kind.includes("refine")) return "#dcfce7";
    if (kind.includes("voice")) return "#fef3c7";
    return "#f1f5f9";
  }};
  color: ${({ kind }) => {
    if (kind.includes("cleanup")) return "#1d4ed8";
    if (kind.includes("refine")) return "#166534";
    if (kind.includes("voice")) return "#92400e";
    return "#475569";
  }};
`;

const TruncatedText = styled.span`
  display: block;
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding: 12px 0;
`;

const PageInfo = styled.span`
  font-size: 14px;
  color: #64748b;
`;

const PageButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const PageButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: ${({ disabled }) => (disabled ? "#f1f5f9" : "#fff")};
  color: ${({ disabled }) => (disabled ? "#94a3b8" : "#374151")};
  font-size: 14px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const LoadingState = styled.div`
  padding: 48px;
  text-align: center;
  color: #64748b;
  background: #f8fafc;
  border-radius: 8px;
`;

const EmptyState = styled.div`
  padding: 48px;
  text-align: center;
  color: #64748b;
  background: #f8fafc;
  border-radius: 8px;
`;

const columnHelper = createColumnHelper<NoteSuggestionSummaryInternalResponseSchema>();

const columns = [
  columnHelper.accessor("id", {
    header: "ID",
    cell: info => <IdCell>{info.getValue().slice(0, 8)}...</IdCell>,
  }),
  columnHelper.accessor("kind", {
    header: "Kind",
    cell: info => <KindBadge kind={info.getValue()}>{info.getValue()}</KindBadge>,
  }),
  columnHelper.accessor("suggested_primary_label", {
    header: "Suggested Label",
    cell: info => <TruncatedText>{info.getValue() || "(empty)"}</TruncatedText>,
  }),
  columnHelper.accessor("note_id", {
    header: "Note ID",
    cell: info => <IdCell>{info.getValue().slice(0, 8)}...</IdCell>,
  }),
  columnHelper.accessor("created_at", {
    header: "Created",
    cell: info => new Date(info.getValue()).toLocaleString(),
  }),
];

const NOTE_SUGGESTION_KINDS = [
  { value: "", label: "All Kinds" },
  { value: "NOTE_CLEANUP_V1", label: "Note Cleanup" },
  { value: "NOTE_CLEANUP_REFINE_V1", label: "Note Cleanup Refine" },
  { value: "VOICE_SESSION_V1", label: "Voice Session" },
  { value: "VOICE_SESSION_REFINE_V1", label: "Voice Session Refine" },
];

export const NoteSuggestionsRootPage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 25;

  const searchQuery = useQuery({
    queryKey: ["note-suggestions-search", query, kindFilter, offset],
    queryFn: () =>
      searchNoteSuggestionsInternal({
        query: query || null,
        filter_by_kind: kindFilter || null,
        limit,
        offset,
      }),
    staleTime: 30 * 1000,
  });

  const noteSuggestions = searchQuery.data?.results ?? [];
  const total = searchQuery.data?.total ?? 0;

  const table = useReactTable({
    data: noteSuggestions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleRowClick = (noteSuggestionId: string) => {
    navigate(atlasRoutes.noteSuggestionsView({ params: { noteSuggestionId } }).path);
  };

  const handlePrevPage = () => {
    setOffset(Math.max(0, offset - limit));
  };

  const handleNextPage = () => {
    if (offset + limit < total) {
      setOffset(offset + limit);
    }
  };

  return (
    <PageContainer>
      <Header>
        <Title>Note Suggestions</Title>
      </Header>

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search by ID or label..."
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setOffset(0);
          }}
        />
        <FilterSelect
          value={kindFilter}
          onChange={e => {
            setKindFilter(e.target.value);
            setOffset(0);
          }}
        >
          {NOTE_SUGGESTION_KINDS.map(kind => (
            <option key={kind.value} value={kind.value}>
              {kind.label}
            </option>
          ))}
        </FilterSelect>
      </SearchContainer>

      {searchQuery.isLoading ? (
        <LoadingState>Loading note suggestions...</LoadingState>
      ) : noteSuggestions.length === 0 ? (
        <EmptyState>No note suggestions found.</EmptyState>
      ) : (
        <>
          <Table>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <Th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </Th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <Tr key={row.id} onClick={() => handleRowClick(row.original.id)}>
                  {row.getVisibleCells().map(cell => (
                    <Td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))}
            </tbody>
          </Table>

          <Pagination>
            <PageInfo>
              Showing {offset + 1}-{Math.min(offset + limit, total)} of {total} note suggestions
            </PageInfo>
            <PageButtons>
              <PageButton onClick={handlePrevPage} disabled={offset === 0}>
                Previous
              </PageButton>
              <PageButton onClick={handleNextPage} disabled={offset + limit >= total}>
                Next
              </PageButton>
            </PageButtons>
          </Pagination>
        </>
      )}
    </PageContainer>
  );
};
